import {
    Component, EventEmitter, forwardRef, Input,
    OnDestroy, OnInit, Output, ViewChild, AfterViewInit
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, share } from 'rxjs/operators';

import { User } from '@data/models/user.model';
import { UserService } from '@data/services/user.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

// Import internal
@Component({

    selector: 'syn-user-picker',
    templateUrl: 'user-picker.component.html',
    styleUrls: ['user-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UserPickerComponent),
            multi: true
        }
    ]
})
export class UserPickerComponent implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit {

    @Output() userChange = new EventEmitter<User>();

    @Input() placeholder: string;
    @Input() emitAndClear = false;
    @Input() isBot = false;
    @Input() disabled = false;
    @Input() required = false;

    @ViewChild(MatAutocompleteTrigger, { static: true }) trigger;

    _userId: string;
    _userType: string;
    _accountType: string;
    selectedUser: User;
    filteredUsers: Observable<User[]>;
    searchTerm: FormControl;

    constructor(private userService: UserService) {
        this.searchTerm = new FormControl();
    }

    // TODO: use tags to distinguish service from user account
    @Input()
    get userType() {
        return this._userType;
    }

    @Input()
    get accountType() {
        return this._accountType;
    }

    @Input('value')
    get userId() {
        return this._userId;
    }


    set userType(val) {
        switch (val) {
            case 'internal':
            case 'external':
                this._userType = val;
                break;
            default:
                this._userType = 'all';
                break;
        }
    }

    set accountType(val) {
        switch (val) {
            case 'user':
            case 'service':
                this._accountType = val;
                break;
            default:
                this._accountType = 'all';
                break;
        }
    }

    set userId(val) {
        if (val && !this.selectedUser) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.userService
                .getUser(val, { includeRemoved: true })
                .toPromise()
                .then(user => {
                    this.selectedUser = user;
                    this.searchTerm.setValue(user);
                    this.userChange.emit(user);
                });
        }
        this._userId = val;
        this.onChange(val);
        this.onTouched();
    }

    onChange: any = () => { };
    onTouched: any = () => { };

    ngOnInit() {
        this.filteredUsers = this.searchTerm.valueChanges.pipe(
            debounceTime(600),
            distinctUntilChanged(), // ignore if same as previous
            switchMap((term) => {   // Be careful, term is the search string or a user object
                if (!term || term instanceof Object) {
                    return of([]);
                }
                term = term.replace(/"/g, '');
                if (term.length > 0) {
                    term += ' '; // AND operator for the query
                }
                const myFilter = [`${term}isBot:${this.isBot}`];
                if (this.userType && this.userType !== 'all') {
                    myFilter.push(`isInternal:${this.userType === 'internal'}`);
                }
                return this.userService.findUsers({ filter: myFilter.join(' '), limit: 10 });
            }),
            share() // mandatory to prevent duplicate http calls
        );
    }

    ngOnDestroy() {
        return;
    }

    ngAfterViewInit() {
        // Set selected user on blur
        this.trigger.panelClosingActions
            .subscribe(e => {
                if (!(e && e.source)) {
                    this.searchTerm.setValue(this.selectedUser);
                    this.trigger.closePanel();
                }
            });
    }

    public registerOnChange(fn) {
        this.onChange = fn;
    }

    public registerOnTouched(fn) {
        this.onTouched = fn;
    }

    public writeValue(userId) {
        if (userId) {
            this.userId = userId;
        }
    }

    public onSelectionChange(event: any, user?: User) {
        // change is fired twice : first with the selected, then the previous one
        if (event.source && event.source.selected) {
            this.selectedUser = user;
            this.userId = user ? user.id : '';
            this.userChange.emit(user);
            if (this.emitAndClear) {
                this.searchTerm.setValue(null);
                this.selectedUser = null;
                this.userId = '';
            }
        }
    }

    public displayFn(user: User) {
        return user ? `${user.firstName} ${user.lastName}` : '';
    }

    // Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (this.disabled) {
            this.searchTerm.disable();
        } else {
            this.searchTerm.enable();
        }
    }

}

